import { SellerGroupUserPermissions } from '@seller-hermes/features/seller-group/seller-group.types';
import type { SellerGroupUser } from '@seller-hermes/features/seller-group/seller-group.types';
import { hasAnyPermission } from '@seller-hermes/features/seller-group/user-access';
import {
  CalendarClock,
  CarFront,
  ClockAlert,
  ListRestart,
  Tags,
  TriangleAlert,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface NavAutomationItem {
  title: string;
  url: string;
  icon: LucideIcon;
  show: boolean;
  items: { title: string; url: string }[];
}

export interface NavAutomationsSection {
  title: string;
  show: boolean;
  items: NavAutomationItem[];
}

/**
 * Seção "Automações" do menu lateral: as regras automáticas que saíram de
 * "Configurações" (Workflows segue lá). Função pura pra ser testável sem
 * montar o sidebar. A seção só aparece (`show`) se algum item for visível,
 * porque o `NavMain` desenha o título mesmo sem itens.
 */
export const buildNavAutomations = (
  user: SellerGroupUser,
  t: (key: string) => string,
): NavAutomationsSection => {
  const items: NavAutomationItem[] = [
    {
      title: t('tags-rules'),
      url: '/tags-rules',
      icon: Tags,
      show: hasAnyPermission(user, [SellerGroupUserPermissions.TAGS_RULE]),
      items: [],
    },
    {
      title: t('dispatch-rules'),
      url: '/dispatch-rules',
      icon: Zap,
      show: hasAnyPermission(user, [
        SellerGroupUserPermissions.DISPATCH_RULE_TO_CARRIER,
      ]),
      items: [],
    },
    {
      title: t('resend-rules'),
      url: '/resend-rules',
      icon: ListRestart,
      show: hasAnyPermission(user, [
        SellerGroupUserPermissions.RESEND_RULE_TO_CARRIER,
      ]),
      items: [],
    },
    {
      title: t('inactivity-rules'),
      url: '/inactivity-rules',
      icon: ClockAlert,
      show: hasAnyPermission(user, [
        SellerGroupUserPermissions.INACTIVITY_RULE_TO_CARRIER,
      ]),
      items: [],
    },
    {
      // Régua do meio da esteira: roda depois da automação de marcador e
      // antes da automação de risco de atraso — por isso fica entre as duas
      // no menu.
      title: t('promised-date-rules'),
      url: '/promised-date-rules',
      icon: CalendarClock,
      // CRUD é INTERNAL/MASTER (proxy no dashboard-api); espelha o guard.
      show: user.type === 'MASTER' || user.type === 'INTERNAL',
      items: [],
    },
    {
      title: t('delay-risk-tiers'),
      url: '/delay-risk-tiers',
      icon: TriangleAlert,
      // Leitura liberada pra MASTER/INTERNAL/ADMIN (USER de fora); o CRUD
      // no dashboard-api segue exigindo só MASTER/INTERNAL — os botões de
      // escrita na própria tela ficam desabilitados pra ADMIN.
      show:
        user.type === 'MASTER' ||
        user.type === 'INTERNAL' ||
        user.type === 'ADMIN',
      items: [],
    },
    {
      title: t('offer-automation-rules'),
      url: '/offer-automation-rules',
      icon: CarFront,
      show: hasAnyPermission(user, [SellerGroupUserPermissions.CROWDSHIPPING]),
      items: [],
    },
  ];

  return {
    title: t('automations'),
    show: items.some((item) => item.show),
    items,
  };
};
